import { useState } from "react";
import { Button } from "./ui/button";
import { Check } from "lucide-react";
import { patientApi } from "../services/api";
import { toast } from "sonner";

// Row cu 2 butoane toggle CU/FĂRĂ pentru un flag; activul primește culoare (verde/roșu).
const FlagRow = ({ label, value, onChange, testIdPrefix, disabled }) => {
  const isCu = value === "cu";
  const isFara = value === "fara";
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap" data-testid={`${testIdPrefix}-row`}>
      <span className="text-sm font-medium">{label}</span>
      <div className="inline-flex rounded-md border overflow-hidden shrink-0">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(isCu ? "" : "cu")}
          className={`px-3 py-1.5 text-xs font-medium transition inline-flex items-center gap-1 ${
            isCu
              ? "bg-emerald-600 text-white"
              : "bg-white hover:bg-emerald-50 text-emerald-700"
          } disabled:opacity-60`}
          data-testid={`${testIdPrefix}-cu`}
        >
          {isCu && <Check className="w-3 h-3" />}
          CU {label.toUpperCase()}
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(isFara ? "" : "fara")}
          className={`px-3 py-1.5 text-xs font-medium border-l transition inline-flex items-center gap-1 ${
            isFara
              ? "bg-red-600 text-white"
              : "bg-white hover:bg-red-50 text-red-700"
          } disabled:opacity-60`}
          data-testid={`${testIdPrefix}-fara`}
        >
          {isFara && <Check className="w-3 h-3" />}
          FĂRĂ {label.toUpperCase()}
        </button>
      </div>
    </div>
  );
};

// Row 3 opțiuni pentru Analize: CU / FĂRĂ / PROPRII (aduse de pacient).
const AnalizeRow = ({ value, onChange, disabled }) => {
  const isCu = value === "cu";
  const isFara = value === "fara";
  const isProprii = value === "proprii";
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="flag-analize-row">
      <span className="text-sm font-medium">Analize</span>
      <div className="inline-flex rounded-md border overflow-hidden shrink-0">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(isCu ? "" : "cu")}
          className={`px-3 py-1.5 text-xs font-medium transition inline-flex items-center gap-1 ${
            isCu ? "bg-emerald-600 text-white" : "bg-white hover:bg-emerald-50 text-emerald-700"
          } disabled:opacity-60`}
          data-testid="flag-analize-cu"
        >
          {isCu && <Check className="w-3 h-3" />}
          CU ANALIZE
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(isFara ? "" : "fara")}
          className={`px-3 py-1.5 text-xs font-medium border-l transition inline-flex items-center gap-1 ${
            isFara ? "bg-red-600 text-white" : "bg-white hover:bg-red-50 text-red-700"
          } disabled:opacity-60`}
          data-testid="flag-analize-fara"
        >
          {isFara && <Check className="w-3 h-3" />}
          FĂRĂ ANALIZE
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(isProprii ? "" : "proprii")}
          className={`px-3 py-1.5 text-xs font-medium border-l transition inline-flex items-center gap-1 ${
            isProprii ? "bg-violet-600 text-white" : "bg-white hover:bg-violet-50 text-violet-700"
          } disabled:opacity-60`}
          data-testid="flag-analize-proprii"
        >
          {isProprii && <Check className="w-3 h-3" />}
          ANALIZE PROPRII
        </button>
      </div>
    </div>
  );
};

// Row cu 2 butoane toggle CAS/CONTRACT (fără culori de tip CU/FĂRĂ — două opțiuni distincte).
const CasContractRow = ({ value, onChange, disabled }) => {
  const isCas = value === "cas";
  const isContract = value === "contract";
  return (
    <div className="flex items-center justify-between gap-3 flex-wrap" data-testid="flag-cas-contract-row">
      <span className="text-sm font-medium">Facturare</span>
      <div className="inline-flex rounded-md border overflow-hidden shrink-0">
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(isCas ? "" : "cas")}
          className={`px-3 py-1.5 text-xs font-medium transition inline-flex items-center gap-1 ${
            isCas
              ? "bg-sky-600 text-white"
              : "bg-white hover:bg-sky-50 text-sky-700"
          } disabled:opacity-60`}
          data-testid="flag-cas-contract-cas"
        >
          {isCas && <Check className="w-3 h-3" />}
          CAS
        </button>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onChange(isContract ? "" : "contract")}
          className={`px-3 py-1.5 text-xs font-medium border-l transition inline-flex items-center gap-1 ${
            isContract
              ? "bg-amber-600 text-white"
              : "bg-white hover:bg-amber-50 text-amber-700"
          } disabled:opacity-60`}
          data-testid="flag-cas-contract-contract"
        >
          {isContract && <Check className="w-3 h-3" />}
          CONTRACT
        </button>
      </div>
    </div>
  );
};

/**
 * Selectoare CU/FĂRĂ EKG (doar la Juniori), CU/FĂRĂ Analize (Juniori + Seniori) și
 * CAS/CONTRACT (facturare, independent de categorie).
 * Se salvează imediat la fiecare schimbare prin PUT /api/patients/{id}/flags.
 */
export const PatientFlagsSection = ({ patient, onUpdated }) => {
  const dp = patient?.date_personale || {};
  const isJunior = !!dp.juniori;
  const isSenior = !!dp.seniori;
  const [ekg, setEkg] = useState(patient?.flag_ekg || "");
  const [analize, setAnalize] = useState(patient?.flag_analize || "");
  const [casContract, setCasContract] = useState(patient?.flag_cas_contract || "");
  const [saving, setSaving] = useState(false);

  const persist = async (payload, optimistic) => {
    setSaving(true);
    try {
      const res = await patientApi.updateFlags(patient.id, payload);
      setEkg(res.flag_ekg || "");
      setAnalize(res.flag_analize || "");
      setCasContract(res.flag_cas_contract || "");
      if (onUpdated) {
        onUpdated({
          flag_ekg: res.flag_ekg || "",
          flag_analize: res.flag_analize || "",
          flag_cas_contract: res.flag_cas_contract || "",
        });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Eroare la actualizarea flag-urilor");
      if (optimistic?.revert) optimistic.revert();
    } finally {
      setSaving(false);
    }
  };

  const showEkgOrAnalize = isJunior || isSenior;

  return (
    <div className="space-y-2" data-testid="patient-flags-section">
      {!showEkgOrAnalize && (
        <div className="text-xs text-muted-foreground italic" data-testid="flags-none">
          Selectează în Date Personale <b>Juniori</b> sau <b>Seniori</b> pentru a activa selectoarele CU/FĂRĂ EKG / Analize.
        </div>
      )}
      {isJunior && (
        <FlagRow
          label="EKG"
          value={ekg}
          disabled={saving}
          testIdPrefix="flag-ekg"
          onChange={(newVal) => {
            const prev = ekg;
            setEkg(newVal);
            persist({ flag_ekg: newVal }, { revert: () => setEkg(prev) });
          }}
        />
      )}
      {showEkgOrAnalize && (
        <AnalizeRow
          value={analize}
          disabled={saving}
          onChange={(newVal) => {
            const prev = analize;
            setAnalize(newVal);
            persist({ flag_analize: newVal }, { revert: () => setAnalize(prev) });
          }}
        />
      )}
      <CasContractRow
        value={casContract}
        disabled={saving}
        onChange={(newVal) => {
          const prev = casContract;
          setCasContract(newVal);
          persist({ flag_cas_contract: newVal }, { revert: () => setCasContract(prev) });
        }}
      />
    </div>
  );
};

// Buton exportat pentru re-use dacă e nevoie
export default PatientFlagsSection;
